import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { cn } from "@/utils/cn";
import { useAuth } from "@clerk/clerk-react";
import { IonSpinner } from "@ionic/react";
import { useEffect, useState } from "react";

/** How long the (non-looping) splash GIF takes to play through, in ms. */
const SPLASH_GIF_DURATION_MS = 3000;

/** Fade-out duration when the splash lifts to reveal the app underneath, in ms. */
const SPLASH_FADE_DURATION_MS = 400;

/**
 * Safety valve: never keep the splash up longer than this, even if the readiness
 * signal never settles (e.g. a wedged Clerk load). The user drops onto whatever
 * route resolved underneath rather than being trapped on the logo forever.
 */
const SPLASH_MAX_DURATION_MS = 12000;

/**
 * The single boot splash. Rendered once, on top of the whole app, from App's first
 * paint — the app loads in the background underneath it. Because this component mounts
 * once and never remounts during boot, the GIF plays through exactly once. Once it has
 * finished it is replaced by a spinner *only if* the app still isn't ready, then it
 * unmounts the moment the app is ready. So the user sees: one branded animation, an
 * optional spinner, then the app — with none of the intermediate spinner screens
 * (RootRedirect / LazyWrapper / the /splash-screen page) flashing through.
 */
export const AppSplash = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoading: isUserLoading } = useGetUser();

  const [forceHidden, setForceHidden] = useState(false);
  const [gifDone, setGifDone] = useState(false);
  const [unmounted, setUnmounted] = useState(false);

  // Clerk resolved and, for signed-in users, the user record settled (success or error —
  // AuthRouteGuard owns error recovery). This mirrors the conditions the guard uses to
  // have navigated to the real destination.
  const appReady = isLoaded && (!isSignedIn || !isUserLoading);

  // Ready to lift once the app is ready AND the GIF has played through, so the branded
  // animation is never cut off mid-play. Derived during render (not via an effect) so the
  // spinner never flashes for a frame when both become true at once.
  const leaving = forceHidden || (appReady && gifDone);

  useEffect(() => {
    const gifTimer = setTimeout(() => setGifDone(true), SPLASH_GIF_DURATION_MS);
    const maxTimer = setTimeout(() => setForceHidden(true), SPLASH_MAX_DURATION_MS);
    return () => {
      clearTimeout(gifTimer);
      clearTimeout(maxTimer);
    };
  }, []);

  // Once leaving, keep the (now transparent) overlay mounted for the fade duration, then
  // remove it entirely. The app is already rendered underneath, so the fade reveals it.
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => setUnmounted(true), SPLASH_FADE_DURATION_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  if (unmounted) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#130C2D] transition-opacity ease-out",
        leaving ? "pointer-events-none opacity-0" : "opacity-100",
      )}
      style={{ transitionDuration: `${SPLASH_FADE_DURATION_MS}ms` }}
    >
      <div className="flex w-full max-w-sm flex-col items-center gap-8">
        {gifDone ? (
          <IonSpinner name="crescent" className="h-12 w-12 text-white" color="light" />
        ) : (
          <img src="/assets/images/splash-animation.gif" alt="NutriCoach" className="w-[392px]" />
        )}
      </div>
    </div>
  );
};
